import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../components/Toast';
import { getSession, isAdmin } from '../lib/auth';
import { getAdminHealth } from '../lib/admin';
import AdminDbTable from '../components/AdminDbTable';

export default function AdminPage() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [adminHealth, setAdminHealth] = useState(null);
  const [adminHealthError, setAdminHealthError] = useState(null);

  const loadAdminData = async ({ signal } = {}) => {
    setIsLoading(true);

    try {
      const session = await getSession();
      if (!session) {
        navigate('/login', { replace: true });
        return;
      }

      if (!isAdmin(session)) {
        navigate('/', { replace: true });
        return;
      }

      const healthResponse = await getAdminHealth().catch((e) => ({ __error: e }));

      if (signal?.aborted) return;

      if (healthResponse && !healthResponse.__error) {
        setAdminHealth(healthResponse);
        setAdminHealthError(null);
      } else {
        setAdminHealth(null);
        setAdminHealthError(healthResponse?.__error ?? new Error('Health unavailable'));
      }

      setLastUpdatedAt(new Date().toISOString());
    } catch (e) {
      console.error('Error loading admin dashboard:', e);
      showToast('Failed to load admin dashboard data. Please try again.', { kind: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadAdminData({ signal: controller.signal });
    return () => controller.abort();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-NBNA-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0E4069]">Admin Dashboard</h1>
          {lastUpdatedAt && (
            <p className="text-sm text-gray-500 mt-1">Last updated: {new Date(lastUpdatedAt).toLocaleString()}</p>
          )}
        </div>
        <button type="button" onClick={() => loadAdminData()} className="btn-secondary">
          Refresh
        </button>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-[#0E4069] mb-4">System Health</h2>

        <div className="mb-4 rounded-lg bg-white shadow p-4">
          <div className="font-medium text-gray-900">Live Metrics (last 5 minutes)</div>
          {adminHealthError || !adminHealth?.metrics ? (
            <div className="mt-1 text-sm text-gray-600">Metrics unavailable.</div>
          ) : (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="rounded border p-3">
                <div className="text-xs text-gray-500">Requests/min</div>
                <div className="text-lg font-semibold">{Number(adminHealth.metrics.apiRequestsPerMinute ?? 0)}</div>
              </div>
              <div className="rounded border p-3">
                <div className="text-xs text-gray-500">p50 latency (ms)</div>
                <div className="text-lg font-semibold">{Number(adminHealth.metrics.apiLatencyP50Ms ?? 0).toFixed(0)}</div>
              </div>
              <div className="rounded border p-3">
                <div className="text-xs text-gray-500">p95 latency (ms)</div>
                <div className="text-lg font-semibold">{Number(adminHealth.metrics.apiLatencyP95Ms ?? 0).toFixed(0)}</div>
              </div>
              <div className="rounded border p-3">
                <div className="text-xs text-gray-500">Queue depth</div>
                <div className="text-lg font-semibold">{Number(adminHealth.metrics.queueDepth ?? 0)}</div>
              </div>
              <div className="rounded border p-3">
                <div className="text-xs text-gray-500">Window (min)</div>
                <div className="text-lg font-semibold">{Number(adminHealth.metrics.scopeMinutes ?? 5)}</div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="rounded-lg bg-white shadow p-4">
            <div className="font-medium text-gray-900">Reset Password / Account Rescue</div>
            <div className="text-sm text-gray-600">Send reset links and assist with account recovery.</div>
          </div>
          <div className="rounded-lg bg-white shadow p-4">
            <div className="font-medium text-gray-900">Integrations / API Keys</div>
            <div className="text-sm text-gray-600">Configure external services and credentials.</div>
          </div>
          <button
            type="button"
            className="rounded-lg bg-white shadow p-4 text-left hover:bg-gray-50 transition-colors"
            onClick={() => navigate('/executive')}
          >
            <div className="font-medium text-gray-900">Executive Dashboard</div>
            <div className="text-sm text-gray-600">View the executive dashboard.</div>
          </button>
        </div>
      </section>

      {/* DB Tables (DEV/OPS) */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-[#0E4069] mb-4">DB Tables (DEV/OPS)</h2>
        <AdminDbTable />
      </section>
    </div>
  );
}
